// ---------------------------------------------------------------------------
// The shape of a whole room, on one strip, with your own score marked.
//
// A top-ten standing tells five hundred people who is winning and tells four
// hundred and ninety of them nothing about themselves. Isaac asked for the shape
// of the room to be visible beside it — a strip of ticks, or a small violin —
// so that a player outside the ten can see where they sit in the pile rather
// than only that they are not at the top of it.
//
// **Divs, never SVG**, which is the house rule and not a preference here: this
// is a column per bucket with a height, and a `<div>` with a percentage height
// is the whole drawing. See `Histogram.tsx`, `PieClock`, and the note in
// CLAUDE.md.
//
// **It is not a `Histogram`, and the difference is what it is read for.** A
// histogram is a series of labelled numbers and degrades to a table of them,
// which is why that component IS a table. This is a silhouette: nobody reads the
// eleventh bucket's count off it, they read whether the pile is in front of them
// or behind them. So the bars are `aria-hidden` and what a screen reader gets is
// the sentence that silhouette is standing in for — lowest, highest, where you
// are, and how many you are ahead of — which are the four facts, said properly.
// A table of twenty-four range buckets would be an accessible rendering of the
// wrong thing.
//
// Three details are load-bearing:
//
//   The track is `bg-raised`, an OPAQUE fill. A translucent track inside a
//   tinted card composites two washes deep and falls off the bottom of what the
//   palette was solved for — CLAUDE.md, and it is the same reason `Histogram`
//   does it.
//
//   Every bucket keeps a baseline nub, including the empty ones. A gap in a
//   distribution is data: the run of scores nobody landed on is exactly what
//   makes the pile a shape rather than a block, and a bucket drawn at zero
//   height with nothing at all under it reads as the strip having ended.
//
//   Your own bucket is `bg-accent` against the others' `bg-blue-400`. Both clear
//   3:1 on the track and `palette.test.ts` holds them there; `blue-500` does not,
//   which is why the bar fills in this directory are the four they are.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';

/** How many columns the strip is cut into at most. More is noise at this height. */
const MAX_BUCKETS = 24;

/** Below this a column is invisible, which reads as a gap that is not there. */
const MIN_VISIBLE_PERCENT = 8;

export interface Bucket {
  /** Inclusive low end of the range this column covers. */
  from: number;
  /** Inclusive high end. Equal to `from` when the range is one score wide. */
  to: number;
  count: number;
  /** The viewer's own score falls in this column. */
  yours: boolean;
}

/**
 * Cut a set of scores into at most `MAX_BUCKETS` columns.
 *
 * Exported for its own test: the bucketing is the only thing here that can be
 * wrong in a way a screenshot would not show, and the case that matters is a
 * room where everybody has the same score — a zero-wide range, which a naive
 * `(value - min) / (max - min)` turns into a division by zero and a strip of
 * NaNs.
 */
export function bucketScores(values: number[], you: number | null): Bucket[] {
  if (values.length === 0) return [];

  const low = Math.min(...values);
  const high = Math.max(...values);
  const span = high - low;

  // One column. Everybody is level, which is an ordinary state at the start of a
  // game and must not read as a broken chart.
  if (span === 0) {
    return [{ from: low, to: low, count: values.length, yours: you !== null && you === low }];
  }

  // Integer-wide columns, so a column covers whole scores rather than a
  // fractional range nothing can land in the middle of.
  const width = Math.max(1, Math.ceil((span + 1) / MAX_BUCKETS));
  const columns = Math.ceil((span + 1) / width);

  const buckets: Bucket[] = Array.from({ length: columns }, (_, index) => ({
    from: low + index * width,
    to: low + index * width + width - 1,
    count: 0,
    yours: false,
  }));

  const indexOf = (value: number) =>
    Math.min(columns - 1, Math.max(0, Math.floor((value - low) / width)));

  for (const value of values) buckets[indexOf(value)].count += 1;
  if (you !== null && you >= low && you <= high) buckets[indexOf(you)].yours = true;

  return buckets;
}

export function ScoreSpread({
  values,
  you = null,
  caption,
}: {
  /** Every seat's total, one entry per seat. Order is irrelevant. */
  values: number[];
  /** The viewer's own total, or null for somebody with no seat in the room. */
  you?: number | null;
  caption: string;
}): ReactElement | null {
  const buckets = bucketScores(values, you);
  if (buckets.length === 0) return null;

  const tallest = buckets.reduce((highest, bucket) => Math.max(highest, bucket.count), 0);
  const low = buckets[0].from;
  const high = buckets[buckets.length - 1].to;
  const behind = you === null ? 0 : values.filter((value) => value < you).length;

  return (
    <div className="flex flex-col gap-1" data-chart="score-spread">
      <div className="flex items-baseline gap-2">
        <span className="label-mono">{caption}</span>
        {you === null ? null : (
          <span className="ml-auto font-mono text-[11px] text-ink-dim tabular">
            you: {you}
          </span>
        )}
      </div>

      {/* The silhouette. Every column is the same width and the row is the
          track, so the strip is one opaque fill with bars standing on it
          rather than a bar per column each carrying its own background. */}
      <div
        aria-hidden="true"
        className="flex h-10 w-full items-end gap-px overflow-hidden rounded-card bg-raised px-1 pb-1"
      >
        {buckets.map((bucket) => {
          const share = tallest === 0 ? 0 : (bucket.count / tallest) * 100;
          const height = bucket.count === 0 ? 0 : Math.max(share, MIN_VISIBLE_PERCENT);
          return (
            <div
              key={bucket.from}
              data-bucket={bucket.from}
              data-count={bucket.count}
              data-you={bucket.yours ? 'true' : undefined}
              className="flex h-full min-w-0 flex-1 flex-col justify-end"
            >
              <div
                className={`w-full rounded-t-[2px] transition-[height] duration-300 ${
                  bucket.yours ? 'bg-accent' : 'bg-blue-400'
                }`}
                style={{ height: `${height}%` }}
              />
              {/* The baseline nub, on every column including the empty ones, so
                  a run of scores nobody reached reads as a gap in the pile
                  rather than as the end of the strip. */}
              <div
                className={`h-px w-full ${bucket.yours ? 'bg-accent' : 'bg-line-strong'}`}
              />
            </div>
          );
        })}
      </div>

      <p className="sr-only">
        {`${caption}: ${values.length} ${values.length === 1 ? 'score' : 'scores'}, from ${low} to ${high}.`}
        {you === null
          ? ''
          : ` You are on ${you}, ahead of ${behind} of ${values.length}.`}
      </p>
    </div>
  );
}
